import { useState } from 'react'
import { cn } from '@/lib/utils'
import { formatNumber, formatCurrency, formatDate } from '@/frontend/pages/CommandCenter/Telemetry/utils'
import { Copy, Check } from 'lucide-react'
import type { Session } from '../types'

interface Props {
  session: Session
  isActive: boolean
  onClick: () => void
}

export default function SessionListItem({ session, isActive, onClick }: Props) {
  const [copied, setCopied] = useState(false)

  const displayName = session.contactName || session.label || 'Indefinido'

  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation()
    navigator.clipboard.writeText(session.sessionId)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <button
      onClick={onClick}
      className={cn(
        'group w-full text-left px-3 py-2.5 border-l-2 transition-colors hover:bg-accent',
        isActive
          ? 'bg-violet-50 dark:bg-violet-500/15 border-l-violet-500'
          : 'border-l-transparent'
      )}
    >
      {/* Row 1: Name + Timestamp */}
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium text-foreground truncate">{displayName}</span>
        <span className="text-[10px] text-muted-foreground whitespace-nowrap flex-shrink-0">
          {session.lastEventAt ? formatDate(session.lastEventAt) : '—'}
        </span>
      </div>

      {/* Row 2: Stats + Model */}
      <div className="flex items-center justify-between gap-2 mt-0.5">
        <span className="text-[10px] text-muted-foreground">
          {session.totalEvents} ev · {formatNumber(session.totalTokens)} tk · {formatCurrency(session.totalCost)}
        </span>
        {session.model && (
          <span className="text-[10px] text-muted-foreground truncate flex-shrink-0">{session.model}</span>
        )}
      </div>

      {/* Row 3: Session ID (click to copy) */}
      <div className="flex items-center justify-end gap-1.5 mt-0.5">
        <span
          onClick={handleCopy}
          className="inline-flex items-center gap-0.5 font-mono text-[10px] text-muted-foreground hover:text-muted-foreground cursor-pointer transition-colors"
          title="Copiar session ID"
        >
          {session.sessionId.slice(0, 8)}
          {copied ? <Check className="h-2.5 w-2.5 text-green-500" /> : <Copy className="h-2.5 w-2.5" />}
        </span>
      </div>
    </button>
  )
}
